<script lang="ts" context="module">
    export const name = "Default gallery";
</script>

<script lang="ts">
    import { mount, onMount, unmount } from "svelte";
    import { fade } from "svelte/transition";
    import ImageView from "@reperio-news/core.components/ImageView.svelte";
    import Data from "@reperio-news/core.components/editorBlocks/ImageGallery/Data";
    import { mediaCredit } from "@reperio-news/core.components/includes/Media.js";
    import { swipe } from "svelte-gestures";
    import Fullscreen from "./Fullscreen.svelte";
    import NavButton from "./NavButton.svelte";

    import { z } from "zod";
    export let id: string;
    export let data: z.infer<typeof Data>;

    let dom: HTMLDivElement;
    let itemsDom: HTMLDivElement;
    let index = 0;
    let mainImage = data.images[index];
    // once the reader shows intent (hover/touch/nav), pre-fetch neighbours
    let warm = false;

    $: mainImage = data.images[index];

    function warmup() {
        warm = true;
    }

    function track() {
        if (__ADMIN__) {
            return;
        }

        const url = new URL(window.location.href);
        const ha = new URLSearchParams(url.hash.substring(1));
        ha.set("gal", id);
        ha.set("gal_i", index.toString());
        ha.set("gal_fs", "0");
        url.hash = ha.toString();
        window.history.replaceState({}, "", url);

        // @ts-ignore
        if (typeof SATI_TrackView === "function") {
            // @ts-ignore
            SATI_TrackView(document.location.href, document.referrer);
        }
    }

    function goTo(i: number): void {
        if (i === index || !data.images || !data.images[i]) {
            return;
        }

        warm = true;
        index = i;
        scrollThumbIntoView();
        track();
    }

    function nav(direction: -1 | 1): void {
        goTo(index + direction);
    }

    function scrollThumbIntoView(smooth = true): void {
        if (!itemsDom) {
            return;
        }

        const thumb = itemsDom.children[index] as HTMLElement;
        if (!thumb) {
            return;
        }

        itemsDom.scrollTo({
            left:
                thumb.offsetLeft -
                (itemsDom.clientWidth - thumb.offsetWidth) / 2,
            behavior: smooth ? "smooth" : "auto",
        });
    }

    const handleSwipe = (e: any) => {
        switch (e.detail.direction) {
            case "left":
                nav(1);
                break;
            case "right":
                nav(-1);
                break;
        }
    };

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            nav(-1);
        } else if (e.key === "ArrowRight") {
            e.preventDefault();
            nav(1);
        }
    }

    let lastScrollPosition = 0;

    function showFullScreen(): void {
        if (__ADMIN__) {
            return;
        }

        lastScrollPosition = window.scrollY;

        const element = document.createElement("div");
        document.body.appendChild(element);

        const c = mount(Fullscreen, {
            target: element,
            props: {
                data,
                index,
                id,
                onClose: () => {
                    unmount(c);
                    document.body.removeChild(element);
                    window.scrollTo(0, lastScrollPosition);

                    const url = new URL(window.location.href);
                    url.hash = "";
                    window.history.replaceState({}, "", url);
                },
            },
        });
    }

    onMount(() => {
        const url = new URL(window.location.href);
        const ha = new URLSearchParams(url.hash.substring(1));

        if (!__ADMIN__ && ha.get("gal") && ha.get("gal") === id) {
            const setIndex = parseInt(ha.get("gal_i"));
            if (
                setIndex &&
                !isNaN(setIndex) &&
                data.images &&
                data.images[setIndex]
            ) {
                index = setIndex;
                warm = true;
                scrollThumbIntoView(false);
            }

            setTimeout(() => {
                dom.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 1000);

            if (ha.get("gal_fs") === "1") {
                showFullScreen();
            }
        }
    });
</script>

<!-- svelte-ignore missing-declaration -->
{#if data && data.images && data.images.length}
    <!-- svelte-ignore missing-declaration -->

    <!-- svelte-ignore a11y_no_noninteractive_tabindex a11y_no_noninteractive_element_interactions -->
    <div
        class="gallery"
        bind:this={dom}
        role="group"
        aria-roledescription="galerie foto"
        aria-label={mainImage.title || "Galerie foto"}
        tabindex="0"
        on:keydown={handleKeydown}
    >
        <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
        <div
            on:click={showFullScreen}
            on:pointerenter={warmup}
            on:touchstart|passive={warmup}
            class="main"
            class:clickable={!__ADMIN__}
            use:swipe={{
                timeframe: 300,
                minSwipeDistance: 60,
                touchAction: "pan-y",
            }}
            on:swipe={handleSwipe}
        >
            {#key index}
                <div class="slide" transition:fade={{ duration: 180 }}>
                    <ImageView
                        src={mainImage}
                        widths={[1136, 800, 500]}
                        heights={[800, 560, 350]}
                        fit="contain"
                    />
                </div>
            {/key}

            {#if data.images.length > 1}
                <div class="counter">{index + 1}/{data.images.length}</div>
            {/if}

            {#if data.images[index - 1]}
                <NavButton
                    direction="prev"
                    label="Imaginea anterioară"
                    onClick={() => nav(-1)}
                />
            {/if}

            {#if data.images[index + 1]}
                <NavButton
                    direction="next"
                    label="Imaginea următoare"
                    onClick={() => nav(1)}
                />
            {/if}

            {#if !__ADMIN__}
                <div class="expand" aria-hidden="true">
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <polyline points="15 3 21 3 21 9" />
                        <polyline points="9 21 3 21 3 15" />
                        <line x1="21" y1="3" x2="14" y2="10" />
                        <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                </div>
            {/if}
        </div>

        {#if mainImage.title || mediaCredit(mainImage, mainImage)}
            <div class="caption">
                {#if mainImage.title}{mainImage.title}{/if}
                {#if mediaCredit(mainImage, mainImage)}
                    {@const credit = mediaCredit(mainImage, mainImage)}
                    <span class="source">
                        Foto:
                        {#if credit.url}
                            <a href={credit.url} target="_blank" rel="nofollow noopener">{credit.text}</a>
                        {:else}
                            {credit.text}
                        {/if}
                    </span>
                {/if}
            </div>
        {/if}

        {#if data.images.length > 1}
            <div class="items" bind:this={itemsDom}>
                {#each data.images as image, i}
                    <button
                        type="button"
                        class="thumb"
                        class:selected={i === index}
                        aria-label={`Imaginea ${i + 1} din ${data.images.length}`}
                        aria-current={i === index ? "true" : undefined}
                        on:click={() => goTo(i)}
                    >
                        <ImageView
                            src={image}
                            widths={[85]}
                            heights={[70]}
                            fit="cover"
                        />
                    </button>
                {/each}
            </div>
        {/if}

        {#if warm}
            <div class="preload" aria-hidden="true">
                {#if data.images[index - 1]}
                    <ImageView
                        src={data.images[index - 1]}
                        widths={[1136, 800, 500]}
                        heights={[800, 560, 350]}
                        fit="contain"
                        loading="eager"
                    />
                {/if}
                {#if data.images[index + 1]}
                    <ImageView
                        src={data.images[index + 1]}
                        widths={[1136, 800, 500]}
                        heights={[800, 560, 350]}
                        fit="contain"
                        loading="eager"
                    />
                {/if}
            </div>
        {/if}
    </div>
{/if}

<style lang="scss">
    .gallery {
        margin: 0px auto 16px auto;

        &:focus-visible {
            outline: 2px solid var(--contextColor, #888);
            outline-offset: 2px;
        }

        .main {
            aspect-ratio: 800/560;
            background-color: #111;
            position: relative;
            user-select: none;
            overflow: hidden;

            &.clickable {
                cursor: zoom-in;
            }

            .slide {
                position: absolute;
                inset: 0;

                :global(picture),
                :global(img) {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }
            }

            .counter {
                position: absolute;
                top: 10px;
                right: 10px;
                z-index: 2;
                pointer-events: none;
                background-color: rgba(15, 15, 15, 0.6);
                color: #fff;
                font-size: 12px;
                line-height: 1;
                letter-spacing: 0.04em;
                padding: 6px 10px;
                border-radius: 999px;
            }

            .expand {
                position: absolute;
                right: 10px;
                bottom: 10px;
                z-index: 2;
                pointer-events: none;
                width: 32px;
                height: 32px;
                display: flex;
                justify-content: center;
                align-items: center;
                border-radius: 50%;
                background-color: rgba(15, 15, 15, 0.6);
                color: #fff;

                svg {
                    width: 16px;
                    height: 16px;
                }
            }
        }

        .caption {
            background-color: var(--contextColor, rgb(50, 48, 48));
            color: #fff;
            font-size: 14px;
            line-height: 1.4;
            padding: 8px 12px;
            text-align: center;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .caption .source {
            opacity: 0.75;
            font-size: 0.9em;
            margin-left: 0.25em;
        }

        .caption .source a {
            color: inherit;
            text-decoration: underline;
        }

        .items {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px;
            overflow-x: auto;
            scroll-snap-type: x proximity;
            scroll-padding-inline: 10px;
            scrollbar-width: none;
            background-color: rgb(240, 236, 236);

            &::-webkit-scrollbar {
                display: none;
            }

            .thumb {
                flex: 0 0 auto;
                width: 85px;
                height: 70px;
                padding: 0;
                border: 2px solid transparent;
                border-radius: 4px;
                overflow: hidden;
                background: none;
                cursor: pointer;
                opacity: 0.55;
                scroll-snap-align: center;
                transition:
                    opacity 0.2s ease,
                    border-color 0.2s ease;

                &:hover {
                    opacity: 0.85;
                }

                &:focus-visible {
                    outline: 2px solid var(--contextColor, #555);
                    outline-offset: 1px;
                }

                &.selected {
                    opacity: 1;
                    border-color: var(--contextColor, #111);
                }

                :global(picture),
                :global(img) {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            }
        }

        .preload {
            display: none;
        }
    }

    @media (max-width: 768px) {
        .gallery {
            .caption {
                font-size: 15px;
            }
        }
    }
</style>
